// src/pages/SignupUser.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "services/supabaseClient";

import SectionCard from "components/SectionCard";
import FooterCompact from "components/FooterCompact";
import { TextInput, DropdownSelect } from "components/InputStyled";
import TermsOfServiceModal, {
  TERMS_VERSION,
} from "components/TermsOfServiceModal";

import { logAudit } from "lib/audit";
import evaluateEmailDomain from "lib/emailDomainPolicy";
import logSignupRequest from "lib/logSignupRequest";
import { callFunction } from "lib/edge";
import { validatePassword } from "lib/passwordPolicy";

function normEmail(s) {
  return String(s || "").trim().toLowerCase();
}

function getEmailFromQuery() {
  try {
    const url = new URL(window.location.href);
    return normEmail(url.searchParams.get("email"));
  } catch {
    return "";
  }
}

function SignupUser() {
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    company: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_postcode: "",
    is_financial_pro: "",
  });

  const [hasSession, setHasSession] = useState(false);
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordAlreadySet, setPasswordAlreadySet] = useState(false);

  const [resendEmail, setResendEmail] = useState(() => getEmailFromQuery());
  const [resendSent, setResendSent] = useState(false);
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0);

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const navigate = useNavigate();

  const baseRedirectTo = useMemo(() => {
    return `${window.location.origin}/signup-user`;
  }, []);

  const passwordInputCls =
    "h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-70";

  useEffect(() => {
    let alive = true;

    async function boot() {
      setError("");

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!alive) return;

      if (!session?.user) {
        setHasSession(false);
        setEmail("");

        const qEmail = getEmailFromQuery();
        if (qEmail) setResendEmail(qEmail);
        return;
      }

      setHasSession(true);
      setEmail(session.user.email || "");
      setPasswordAlreadySet(!!session.user.app_metadata?.initial_password_set_at);
    }

    boot();

    return () => {
      alive = false;
    };
  }, []);

  const handleFieldChange = (field, value) => {
    setProfile((s) => ({ ...s, [field]: value }));
  };

  const canResend = Date.now() >= resendCooldownUntil;

  const validateProfileForm = () => {
    if (!profile.is_financial_pro) {
      return "Please indicate whether you are a registered financial professional.";
    }

    if (!profile.first_name.trim()) return "First name is required.";
    if (!profile.last_name.trim()) return "Last name is required.";
    if (!profile.company.trim()) return "Company is required.";
    if (!profile.city.trim()) return "City is required.";
    if (!profile.state.trim()) return "State is required.";
    if (!profile.zip_postcode.trim()) return "ZIP / Postal Code is required.";

    if (!passwordAlreadySet) {
      const passwordError = validatePassword(password, confirmPassword);
      if (passwordError) return passwordError;
    }

    return "";
  };

  const handleResendSignupLink = async (e) => {
    e.preventDefault();
    setError("");
    setResendSent(false);

    const eaddr = normEmail(resendEmail);

    if (!eaddr) {
      setError("Please enter your email to continue signup.");
      return;
    }

    const domainPolicy = evaluateEmailDomain(eaddr);

    await logSignupRequest({
      email: eaddr,
      domain: domainPolicy.domain,
      source: "signup_user",
      policyResult: domainPolicy.reason ?? "allowed",
    });

    if (domainPolicy.reason === "blocked_domain") {
      setError(
        "Please use your company email address. Personal domains are not allowed."
      );
      return;
    }

    if (domainPolicy.reason === "manual_review_required") {
      setError(
        <>
          Your organization requires manual approval before signup. Please contact{" "}
          <a
            href="mailto:support@core2tek.com"
            className="linkCls"
          >
            support@core2tek.com
          </a>{" "}
          for access.
        </>
      );
    }

    if (!domainPolicy.allowed) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!canResend) {
      setError("Please wait a moment before sending another link.");
      return;
    }

    setResending(true);

    try {
      const redirectTo = `${baseRedirectTo}?email=${encodeURIComponent(eaddr)}`;

      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: eaddr,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: redirectTo,
        },
      });

      if (otpErr) {
        setError("Error sending signup link: " + otpErr.message);

        await logAudit({
          category: "auth",
          action: "signup_email_failed",
          success: false,
          detail: {
            email: eaddr,
            error: otpErr.message,
            source: "signup_user_resend",
          },
        });

        return;
      }

      setResendSent(true);
      setResendCooldownUntil(Date.now() + 60_000);

      await logAudit({
        category: "auth",
        action: "signup_email_sent",
        success: true,
        detail: { email: eaddr, source: "signup_user_resend" },
      });
    } finally {
      setResending(false);
    }
  };

  const completeSignup = async () => {
    setLoading(true);
    setError("");

    const validationError = validateProfileForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      setError("User not authenticated. Please request a new signup link.");

      await logAudit({
        category: "user",
        action: "profile_failed",
        success: false,
        detail: { error: "User not authenticated" },
      });

      setLoading(false);
      return;
    }

    if (!passwordAlreadySet) {
      const { error: pwError } = await supabase.auth.updateUser({ password });

      if (pwError) {
        setError("Unable to set the password. Please review the password rules and try again.");

        await logAudit({
          category: "user",
          action: "profile_failed",
          success: false,
          detail: { email: user.email, id: user.id, error: pwError.message },
        });

        setLoading(false);
        return;
      }
    }

    const { data: tenant, error: tenantError } = await supabase
      .from("tenants")
      .select("id")
      .eq("slug", "grm")
      .single();

    if (tenantError || !tenant) {
      const msg = tenantError?.message ?? "Tenant not found";
      setError("Error resolving tenant: " + msg);

      await logAudit({
        category: "user",
        action: "profile_failed",
        success: false,
        detail: { email: user.email, id: user.id, error: msg },
      });

      setLoading(false);
      return;
    }

    const { error: upsertError } = await supabase.from("profiles").upsert(
      {
        id: user.id,
        email: user.email,
        tenant_id: tenant.id,
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim(),
        company: profile.company.trim(),
        phone: profile.phone.trim(),
        address: profile.address.trim(),
        city: profile.city.trim(),
        state: profile.state.trim(),
        zip_postcode: profile.zip_postcode.trim(),
        is_financial_pro: profile.is_financial_pro === "yes",
        terms_accepted_at: new Date().toISOString(),
        terms_version: TERMS_VERSION,
      },
      { onConflict: "id" }
    );

    if (upsertError) {
      setError("Error saving profile: " + upsertError.message);

      await logAudit({
        category: "user",
        action: "profile_failed",
        success: false,
        detail: {
          email: user.email,
          id: user.id,
          error: upsertError.message,
        },
      });

      setLoading(false);
      return;
    }

    const { error: utError } = await supabase
      .from("user_tenants")
      .insert({
        user_id: user.id,
        tenant_id: tenant.id,
        role: "member",
      })
      .select()
      .single();

    if (utError && utError.code !== "23505") {
      setError("Error linking user to tenant: " + utError.message);

      await logAudit({
        category: "user",
        action: "user_tenant_failed",
        success: false,
        detail: {
          email: user.email,
          id: user.id,
          error: utError.message,
        },
      });

      setLoading(false);
      return;
    }

    if (passwordAlreadySet) {
      try {
        await callFunction("invite-user", { body: { action: "complete_profile" } });
      } catch {
        setError("Your profile was saved, but organization access could not be finalized. Please contact your Compass administrator.");
        setLoading(false);
        return;
      }
    }

    await logAudit({
      category: "user",
      action: "profile_completed",
      success: true,
      detail: {
        email: user.email,
        id: user.id,
        terms_version: TERMS_VERSION,
      },
    });

    navigate("/dashboard");
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validateProfileForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setShowTermsModal(true);
  };

  const handleAcceptTerms = async () => {
    setShowTermsModal(false);
    await completeSignup();
  };

  if (!hasSession) {
    return (
      <div className="flex min-h-screen flex-col bg-surface text-app">
        <div className="flex flex-1 items-start justify-center p-6">
          <div className="w-full max-w-xl">
            <h1 className="text-2xl font-semibold text-brand">
              Continue Signup
            </h1>

            <SectionCard className="mt-4">
              <p className="text-sm text-muted">
                Your original invitation link may have already been used. Enter
                your email and we’ll send a fresh signup link so you can
                continue.
              </p>

              <form onSubmit={handleResendSignupLink} className="mt-4 space-y-4">
                <TextInput
                  label="Email"
                  value={resendEmail}
                  onChange={setResendEmail}
                  className="w-full"
                  disabled={resending}
                />

                <button
                  type="submit"
                  disabled={resending || !canResend}
                  className="btn btn-primary w-full"
                  title={
                    !canResend
                      ? "Please wait before sending another link."
                      : undefined
                  }
                >
                  {resending ? "Sending..." : "Send Signup Link"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/start")}
                  className="btn btn-ghost w-full"
                >
                  Back to Login
                </button>

                {resendSent && (
                  <p className="text-sm text-muted">
                    Link sent. Check your email, and if you don’t see it, check
                    spam.
                  </p>
                )}
              </form>

              {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
            </SectionCard>
          </div>
        </div>

        <FooterCompact />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface text-app">
      <div className="flex flex-1 items-start justify-center p-6">
        <div className="w-full max-w-xl">
          <h1 className="text-2xl font-semibold text-brand">
            Complete Your Profile
          </h1>

          <SectionCard className="mt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <TextInput
                label="Email / Username"
                value={email}
                disabled
                className="w-full"
              />

              <DropdownSelect
                label="Are you a registered financial professional?"
                value={profile.is_financial_pro}
                onChange={(v) => handleFieldChange("is_financial_pro", v)}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                placeholder="Select Yes or No"
                className="w-full"
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextInput
                  label="First Name"
                  value={profile.first_name}
                  onChange={(v) => handleFieldChange("first_name", v)}
                  className="w-full"
                />

                <TextInput
                  label="Last Name"
                  value={profile.last_name}
                  onChange={(v) => handleFieldChange("last_name", v)}
                  className="w-full"
                />
              </div>

              <TextInput
                label="Company"
                value={profile.company}
                onChange={(v) => handleFieldChange("company", v)}
                className="w-full"
              />

              <TextInput
                label="Phone"
                value={profile.phone}
                onChange={(v) => handleFieldChange("phone", v)}
                className="w-full"
              />

              <TextInput
                label="Address"
                value={profile.address}
                onChange={(v) => handleFieldChange("address", v)}
                className="w-full"
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <TextInput
                  label="City"
                  value={profile.city}
                  onChange={(v) => handleFieldChange("city", v)}
                  className="w-full"
                />

                <TextInput
                  label="State"
                  value={profile.state}
                  onChange={(v) => handleFieldChange("state", v)}
                  className="w-full"
                />

                <TextInput
                  label="ZIP / Postal Code"
                  value={profile.zip_postcode}
                  onChange={(v) => handleFieldChange("zip_postcode", v)}
                  className="w-full"
                />
              </div>

              {!passwordAlreadySet && <><div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col">
                  <span className="mb-1 text-xs font-medium text-muted-foreground">
                    Password
                  </span>
                  <input
                    type="password"
                    className={passwordInputCls}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                </label>

                <label className="flex flex-col">
                  <span className="mb-1 text-xs font-medium text-muted-foreground">
                    Confirm Password
                  </span>
                  <input
                    type="password"
                    className={passwordInputCls}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                </label>
              </div>

              <div className="text-sm text-muted">
                <p className="mb-1 font-medium">Valid password rules:</p>
                <ul className="list-inside list-disc text-xs">
                  <li>Minimum 10 characters</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                  <li>At least one symbol character</li>
                </ul>
              </div></>}

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? "Saving..." : "Complete Signup"}
              </button>
            </form>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          </SectionCard>
        </div>
      </div>

      <FooterCompact />

      {showTermsModal && (
        <TermsOfServiceModal
          onClose={() => setShowTermsModal(false)}
          onAccept={handleAcceptTerms}
          loading={loading}
        />
      )}
    </div>
  );
}

export default SignupUser;
